import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Target, CheckCircle, Circle, Clock, Beaker, X,
  ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus,
  Filter, BarChart3, ListTodo, AlertTriangle, RefreshCw,
  MapPin, Package, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRIORITY_STYLES = {
  high: { bg: 'bg-red-500', text: 'text-red-700', label: 'HIGH', ring: 'ring-red-200' },
  medium: { bg: 'bg-yellow-500', text: 'text-yellow-700', label: 'MEDIUM', ring: 'ring-yellow-200' },
  low: { bg: 'bg-green-500', text: 'text-green-700', label: 'LOW', ring: 'ring-green-200' },
};

const STATUS_CONFIG = {
  active: { bg: 'bg-blue-100 text-blue-800', label: 'Active', icon: Target, column: 'active' },
  checking: { bg: 'bg-yellow-100 text-yellow-800', label: 'Checking...', icon: RefreshCw, column: 'active' },
  confirmed: { bg: 'bg-green-100 text-green-800', label: 'Confirmed', icon: CheckCircle, column: 'confirmed' },
  refuted: { bg: 'bg-red-100 text-red-800', label: 'Refuted', icon: X, column: 'refuted' },
  neutral: { bg: 'bg-slate-100 text-slate-700', label: 'Neutral', icon: Minus, column: 'completed' },
  too_early: { bg: 'bg-orange-100 text-orange-800', label: 'Too early', icon: Clock, column: 'active' },
  abandoned: { bg: 'bg-slate-200 text-slate-500', label: 'Declined', icon: X, column: 'completed' },
  declined: { bg: 'bg-slate-200 text-slate-500', label: 'Not accepted', icon: X, column: 'completed' },
  closed: { bg: 'bg-slate-200 text-slate-500', label: 'Closed', icon: X, column: 'completed' },
};

const CATEGORY_LABELS = {
  content: 'Content',
  technical: 'Technical',
  external: 'External',
  brand: 'Brand',
};

/* ─── Progress Bar ─── */
const ProgressBar = ({ completed, total }) => {
  if (total === 0) return null;
  const pct = Math.round((completed / total) * 100);
  return (
    <div className="mt-2">
      <div className="flex justify-between text-[10px] text-slate-400 mb-1">
        <span>{completed}/{total} tasks</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-100 overflow-hidden">
        <div className="h-full bg-[#1BB594] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/* ─── Summary Card ─── */
const SummaryCard = ({ label, value, sub, icon: Icon, color = 'text-slate-900' }) => (
  <div className="bg-white border border-[#E2DDD8] p-4">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon className={`w-4 h-4 ${color}`} />}
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
    <p className={`text-2xl font-black ${color}`}>{value}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </div>
);

/* ─── Hypothesis Card (Kanban) ─── */
const HypothesisCard = ({ hyp, onToggleTask, onCheck, onUpdateStatus, onExpand, expanded }) => {
  const priority = PRIORITY_STYLES[hyp.priority] || PRIORITY_STYLES.medium;
  const statusCfg = STATUS_CONFIG[hyp.status] || STATUS_CONFIG.active;
  const tasksTotal = hyp.tasks?.length || 0;
  const tasksDone = hyp.tasks?.filter(t => t.completed).length || 0;
  const lastVerification = hyp.verification_results?.length > 0
    ? hyp.verification_results[hyp.verification_results.length - 1]
    : null;

  return (
    <div className={`bg-white border border-[#E2DDD8] p-4 mb-3 hover:shadow-sm transition-shadow ${expanded ? 'ring-2 ring-[#1BB594]/30' : ''}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-block w-2 h-2 rounded-full ${priority.bg}`} title={priority.label} />
            <span className={`text-[10px] px-1.5 py-0.5 ${statusCfg.bg}`}>{statusCfg.label}</span>
            {hyp.location && (
              <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                <MapPin className="w-3 h-3" />{hyp.location}
              </span>
            )}
            {hyp.product_name && (
              <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                <Package className="w-3 h-3" />{hyp.product_name}
              </span>
            )}
          </div>
          <h4 className="text-sm font-bold text-slate-900 leading-tight">{hyp.title}</h4>
        </div>
        <button onClick={() => onExpand(hyp.id)} className="text-slate-400 hover:text-slate-600 shrink-0">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Engines */}
      {hyp.engines?.length > 0 && (
        <div className="flex gap-1 flex-wrap mb-2">
          {hyp.engines.map(e => (
            <span key={e} className="text-[9px] bg-slate-50 text-slate-500 px-1.5 py-0.5 border border-slate-200">{e}</span>
          ))}
        </div>
      )}

      {/* Task progress */}
      {tasksTotal > 0 && <ProgressBar completed={tasksDone} total={tasksTotal} />}

      {/* Expanded content */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          {/* Fact / Gap / Hypothesis */}
          {hyp.fact && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Fact</p>
              <p className="text-xs text-slate-700">{hyp.fact}</p>
            </div>
          )}
          {hyp.gap && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Gap</p>
              <p className="text-xs text-slate-700">{hyp.gap}</p>
            </div>
          )}
          {hyp.hypothesis_text && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Hypothesis</p>
              <p className="text-xs text-slate-700 italic">{hyp.hypothesis_text}</p>
            </div>
          )}

          {/* Tasks */}
          {tasksTotal > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Tasks</p>
              <div className="space-y-1">
                {hyp.tasks.map(task => (
                  <label key={task.id} className="flex items-start gap-2 cursor-pointer group">
                    <button
                      onClick={(e) => { e.preventDefault(); onToggleTask(hyp.id, task.id, !task.completed); }}
                      className="mt-0.5 shrink-0"
                    >
                      {task.completed
                        ? <CheckCircle className="w-4 h-4 text-[#1BB594]" />
                        : <Circle className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />
                      }
                    </button>
                    <span className={`text-xs ${task.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                      {task.text}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Verification info */}
          {hyp.verification?.metric && (
            <div className="bg-slate-50 p-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Verification</p>
              <p className="text-xs text-slate-600">Metric: <span className="font-medium">{hyp.verification.metric}</span></p>
              {hyp.verification.expected_signal && (
                <p className="text-xs text-slate-600">Expected: {hyp.verification.expected_signal}</p>
              )}
              {hyp.check_date && (
                <p className="text-xs text-slate-500 mt-1">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Check: {new Date(hyp.check_date).toLocaleDateString('en-US')}
                </p>
              )}
            </div>
          )}

          {/* Last verification result */}
          {lastVerification && (
            <div className="bg-blue-50 p-2">
              <p className="text-[10px] uppercase tracking-wider text-blue-600 mb-0.5">Last check</p>
              <div className="flex items-center gap-2 text-xs">
                <span className={`px-1.5 py-0.5 ${STATUS_CONFIG[lastVerification.verdict]?.bg || 'bg-slate-100'}`}>
                  {STATUS_CONFIG[lastVerification.verdict]?.label || lastVerification.verdict}
                </span>
                {lastVerification.delta_pp !== undefined && (
                  <span className={lastVerification.delta_pp >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {lastVerification.delta_pp >= 0 ? '+' : ''}{lastVerification.delta_pp}pp
                  </span>
                )}
                {lastVerification.p_value !== undefined && (
                  <span className="text-slate-400">p={lastVerification.p_value}</span>
                )}
              </div>
              {lastVerification.comment && (
                <p className="text-xs text-slate-600 mt-1 italic">{lastVerification.comment}</p>
              )}
            </div>
          )}

          {/* Actions */}
          {(hyp.status === 'active' || hyp.status === 'too_early') && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => onCheck(hyp.id)}
                className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#1BB594] text-white hover:bg-[#159B7D] transition-colors"
              >
                <Beaker className="w-3 h-3" /> Check
              </button>
              <button
                onClick={() => onUpdateStatus(hyp.id, 'abandoned')}
                className="flex items-center gap-1 text-xs px-3 py-1.5 bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                <X className="w-3 h-3" /> Decline
              </button>
            </div>
          )}
          {hyp.status === 'refuted' && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => onUpdateStatus(hyp.id, 'active')}
                className="text-xs px-3 py-1.5 bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Retry
              </button>
              <button
                onClick={() => onUpdateStatus(hyp.id, 'closed')}
                className="text-xs px-3 py-1.5 bg-white border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════ */
const WorkPlanPage = () => {
  const { monitorId } = useParams();
const navigate = useNavigate();

  const [workplan, setWorkplan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedHyp, setExpandedHyp] = useState(null);
  const [checkingHyp, setCheckingHyp] = useState(null);
  const [viewMode, setViewMode] = useState('kanban'); // kanban | list | location

  // Filters
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterEngine, setFilterEngine] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchWorkplan = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/monitors/${monitorId}/workplan`, { headers });
      setWorkplan(data);
    } catch (err) {
      console.error('Failed to load workplan:', err);
      toast.error('Failed to load work plan');
    } finally {
      setLoading(false);
    }
  }, [monitorId]);

  useEffect(() => {
    fetchWorkplan();
  }, [fetchWorkplan]);

  // ─── Handlers ───
  const handleToggleTask = async (hypId, taskId, completed) => {
    try {
      await axios.patch(`${API_URL}/api/hypotheses/${hypId}/tasks`, { task_id: taskId, completed }, { headers });
      setWorkplan(prev => ({
        ...prev,
        hypotheses: prev.hypotheses.map(h =>
          h.id === hypId
            ? { ...h, tasks: h.tasks.map(t => t.id === taskId ? { ...t, completed } : t) }
            : h
        )
      }));
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const handleCheckHypothesis = async (hypId) => {
    setCheckingHyp(hypId);
    try {
      const { data } = await axios.post(`${API_URL}/api/hypotheses/${hypId}/check`, {}, { headers });
      setWorkplan(prev => ({
        ...prev,
        hypotheses: prev.hypotheses.map(h => h.id === hypId ? { ...h, ...data } : h)
      }));
      toast.success(`Verdict: ${STATUS_CONFIG[data.status]?.label || data.status}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check failed');
    } finally {
      setCheckingHyp(null);
    }
  };

  const handleUpdateStatus = async (hypId, status) => {
    try {
      await axios.patch(`${API_URL}/api/hypotheses/${hypId}`, { status }, { headers });
      setWorkplan(prev => ({
        ...prev,
        hypotheses: prev.hypotheses.map(h => h.id === hypId ? { ...h, status } : h)
      }));
      toast.success(`Status updated: ${STATUS_CONFIG[status]?.label || status}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // ─── Filtering ───
  const filteredHypotheses = (workplan?.hypotheses || []).filter(h => {
    if (filterPriority !== 'all' && h.priority !== filterPriority) return false;
    if (filterLocation !== 'all' && h.location !== filterLocation) return false;
    if (filterCategory !== 'all' && h.category !== filterCategory) return false;
    if (filterEngine !== 'all' && !(h.engines || []).includes(filterEngine)) return false;
    return true;
  });

  // Group by kanban column
  const kanbanColumns = {
    active: { title: 'Active', color: 'border-blue-400', items: [] },
    confirmed: { title: 'Confirmed', color: 'border-green-400', items: [] },
    refuted: { title: 'Refuted', color: 'border-red-400', items: [] },
    completed: { title: 'Completed', color: 'border-slate-300', items: [] },
  };

  filteredHypotheses.forEach(h => {
    const col = STATUS_CONFIG[h.status]?.column || 'completed';
    kanbanColumns[col].items.push(h);
  });

  // Unique values for filters
  const allLocations = [...new Set((workplan?.hypotheses || []).map(h => h.location).filter(Boolean))];
  const allEngines = [...new Set((workplan?.hypotheses || []).flatMap(h => h.engines || []))];
  const allCategories = [...new Set((workplan?.hypotheses || []).map(h => h.category).filter(Boolean))];

  // ─── Render ───
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-[#1BB594] animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading work plan...</p>
        </div>
      </div>
    );
  }

  if (!workplan) {
    return (
      <div className="min-h-screen bg-[#F8F6F3] flex items-center justify-center">
        <p className="text-slate-500">Failed to load work plan</p>
      </div>
    );
  }

  const summary = workplan.summary || {};

  return (
    <div className="min-h-screen bg-[#F8F6F3]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2DDD8] px-4 sm:px-8 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/monitor/${monitorId}`)} className="text-slate-400 hover:text-slate-600">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-black tracking-tight text-[#2F2E2F]">
                WORK PLAN
              </h1>
              <p className="text-sm text-slate-500">{workplan.monitor_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex border border-[#E2DDD8]">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-1.5 text-xs font-bold tracking-wider ${viewMode === 'kanban' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <BarChart3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-xs font-bold tracking-wider ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <ListTodo className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('location')}
                className={`px-3 py-1.5 text-xs font-bold tracking-wider ${viewMode === 'location' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <MapPin className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold border transition-colors ${showFilters ? 'bg-[#1BB594] text-white border-[#1BB594]' : 'bg-white text-slate-600 border-[#E2DDD8] hover:bg-slate-50'}`}
            >
              <Filter className="w-4 h-4" /> Filters
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 py-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          <SummaryCard
            label="Total hypotheses"
            value={summary.total_hypotheses || 0}
            icon={Target}
            color="text-[#2F2E2F]"
          />
          <SummaryCard
            label="Active"
            value={summary.status_counts?.active || 0}
            sub={summary.status_counts?.checking ? `+${summary.status_counts.checking} checking` : undefined}
            icon={Beaker}
            color="text-blue-600"
          />
          <SummaryCard
            label="Confirmed"
            value={summary.status_counts?.confirmed || 0}
            icon={CheckCircle}
            color="text-green-600"
          />
          <SummaryCard
            label="Refuted"
            value={summary.status_counts?.refuted || 0}
            icon={X}
            color="text-red-600"
          />
          <SummaryCard
            label="Tasks"
            value={`${summary.completed_tasks || 0}/${summary.total_tasks || 0}`}
            sub={`${summary.task_progress_pct || 0}% completed`}
            icon={ListTodo}
            color="text-[#1BB594]"
          />
          <SummaryCard
            label="High priority"
            value={summary.priority_counts?.high || 0}
            icon={AlertTriangle}
            color="text-red-500"
          />
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="bg-white border border-[#E2DDD8] p-4 mb-6 flex flex-wrap gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Priority</label>
              <select
                value={filterPriority}
                onChange={e => setFilterPriority(e.target.value)}
                className="text-sm border border-slate-200 px-2 py-1"
              >
                <option value="all">All</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Location</label>
              <select
                value={filterLocation}
                onChange={e => setFilterLocation(e.target.value)}
                className="text-sm border border-slate-200 px-2 py-1"
              >
                <option value="all">All</option>
                {allLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Category</label>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="text-sm border border-slate-200 px-2 py-1"
              >
                <option value="all">All</option>
                {allCategories.map(cat => <option key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">Engine</label>
              <select
                value={filterEngine}
                onChange={e => setFilterEngine(e.target.value)}
                className="text-sm border border-slate-200 px-2 py-1"
              >
                <option value="all">All</option>
                {allEngines.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <button
              onClick={() => { setFilterPriority('all'); setFilterLocation('all'); setFilterCategory('all'); setFilterEngine('all'); }}
              className="self-end text-xs text-slate-500 hover:text-slate-700 underline"
            >
              Reset
            </button>
          </div>
        )}

        {/* Empty state */}
        {filteredHypotheses.length === 0 && (
          <div className="bg-white border border-[#E2DDD8] p-12 text-center">
            <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-700 mb-2">No hypotheses</h3>
            <p className="text-sm text-slate-500">
              Hypotheses will appear after running monitoring. Click "Add to work plan" on a recommendation in monitoring results.
            </p>
          </div>
        )}

        {/* ─── KANBAN VIEW ─── */}
        {viewMode === 'kanban' && filteredHypotheses.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {Object.entries(kanbanColumns).map(([key, col]) => (
              <div key={key}>
                <div className={`border-t-2 ${col.color} bg-white border border-[#E2DDD8] p-3 mb-3`}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-slate-700">{col.title}</h3>
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5">{col.items.length}</span>
                  </div>
                </div>
                <div className="space-y-0">
                  {col.items.map(h => (
                    <HypothesisCard
                      key={h.id}
                      hyp={h}
                      expanded={expandedHyp === h.id}
                      onExpand={id => setExpandedHyp(expandedHyp === id ? null : id)}
                      onToggleTask={handleToggleTask}
                      onCheck={handleCheckHypothesis}
                      onUpdateStatus={handleUpdateStatus}
                    />
                  ))}
                  {col.items.length === 0 && (
                    <div className="border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">
                      Empty
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── LIST VIEW ─── */}
        {viewMode === 'list' && filteredHypotheses.length > 0 && (
          <div className="space-y-3">
            {/* Sort by priority: high → medium → low */}
            {[...filteredHypotheses]
              .sort((a, b) => {
                const order = { high: 0, medium: 1, low: 2 };
                return (order[a.priority] ?? 1) - (order[b.priority] ?? 1);
              })
              .map(h => (
                <HypothesisCard
                  key={h.id}
                  hyp={h}
                  expanded={expandedHyp === h.id}
                  onExpand={id => setExpandedHyp(expandedHyp === id ? null : id)}
                  onToggleTask={handleToggleTask}
                  onCheck={handleCheckHypothesis}
                  onUpdateStatus={handleUpdateStatus}
                />
              ))
            }
          </div>
        )}

        {/* ─── LOCATION VIEW ─── */}
        {viewMode === 'location' && filteredHypotheses.length > 0 && (
          <div className="space-y-6">
            {/* Group by location */}
            {Object.entries(
              filteredHypotheses.reduce((acc, h) => {
                const loc = h.location || 'No location';
                if (!acc[loc]) acc[loc] = [];
                acc[loc].push(h);
                return acc;
              }, {})
            ).map(([location, hyps]) => (
              <div key={location}>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-[#1BB594]" />
                  <h3 className="text-lg font-bold text-[#2F2E2F]">{location}</h3>
                  <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5">{hyps.length}</span>
                  <span className="text-xs text-green-600">
                    {hyps.filter(h => h.status === 'confirmed').length} conf.
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {hyps.map(h => (
                    <HypothesisCard
                      key={h.id}
                      hyp={h}
                      expanded={expandedHyp === h.id}
                      onExpand={id => setExpandedHyp(expandedHyp === id ? null : id)}
                      onToggleTask={handleToggleTask}
                      onCheck={handleCheckHypothesis}
                      onUpdateStatus={handleUpdateStatus}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─── Location breakdown mini-table ─── */}
        {summary.by_location && Object.keys(summary.by_location).length > 1 && (
          <div className="mt-8 bg-white border border-[#E2DDD8] p-6">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">Hypotheses by Location</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 pr-4 text-xs text-slate-500 font-medium">Location</th>
                    <th className="text-center py-2 px-3 text-xs text-slate-500 font-medium">Total</th>
                    <th className="text-center py-2 px-3 text-xs text-blue-500 font-medium">Active</th>
                    <th className="text-center py-2 px-3 text-xs text-green-500 font-medium">Conf.</th>
                    <th className="text-center py-2 px-3 text-xs text-red-500 font-medium">Ref.</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.by_location).map(([loc, counts]) => (
                    <tr key={loc} className="border-b border-slate-100">
                      <td className="py-2 pr-4 font-medium text-slate-700">{loc}</td>
                      <td className="py-2 px-3 text-center text-slate-600">{counts.total}</td>
                      <td className="py-2 px-3 text-center text-blue-600 font-bold">{counts.active}</td>
                      <td className="py-2 px-3 text-center text-green-600 font-bold">{counts.confirmed}</td>
                      <td className="py-2 px-3 text-center text-red-600 font-bold">{counts.refuted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default WorkPlanPage;
